import { useState, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  sendMessage as sendAssistantMessage,
  executeAction as executeAssistantAction,
  type AdminChatContext,
  type SuggestedAction,
  type RelatedPage,
  type ExecuteActionResponse,
} from '../../api/assistantApi';

/**
 * Extract entity type and ID from dashboard URLs.
 * E.g., /dashboard/accounts/123-456 -> { type: 'account', id: '123-456' }
 */
function extractEntityFromPath(pathname: string): { type: string; id: string } | null {
  // Common dashboard entity patterns
  const patterns: [RegExp, string][] = [
    [/\/dashboard\/accounts\/([^/]+)$/, 'customer'],
    [/\/dashboard\/customers\/([^/]+)$/, 'customer'],
    [/\/dashboard\/leads\/([^/]+)$/, 'lead'],
    [/\/dashboard\/invoices\/([^/]+)$/, 'invoice'],
    [/\/dashboard\/payments\/([^/]+)$/, 'payment'],
    [/\/dashboard\/tickets\/([^/]+)$/, 'ticket'],
    [/\/dashboard\/equipment\/([^/]+)$/, 'equipment'],
    [/\/dashboard\/service-areas\/([^/]+)$/, 'serviceArea'],
  ];

  for (const [pattern, entityType] of patterns) {
    const match = pathname.match(pattern);
    if (match && match[1]) {
      return { type: entityType, id: match[1] };
    }
  }

  return null;
}

/**
 * Message in the assistant chat conversation
 */
export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  suggestedAction?: SuggestedAction;
  relatedPages?: RelatedPage[];
}

/**
 * Hook for managing AI assistant chat conversations.
 * Handles message state, loading state, error handling, and conversation persistence.
 */
export function useAssistantChat() {
  const location = useLocation();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [conversationId, setConversationId] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Extract current entity context from URL
  const currentEntity = useMemo(
    () => extractEntityFromPath(location.pathname),
    [location.pathname]
  );

  /**
   * Send a message to the assistant.
   * Adds the user message immediately, then fetches and adds the assistant response.
   */
  const sendMessage = useCallback(
    async (message: string, context?: AdminChatContext) => {
      if (!message.trim()) return;

      // Add user message immediately
      const userMessage: ChatMessage = {
        role: 'user',
        content: message.trim(),
      };
      setMessages((prev) => [...prev, userMessage]);

      setIsLoading(true);
      setError(null);

      try {
        // Build context with current page and entity if on a detail page
        const requestContext: AdminChatContext = {
          currentPage: location.pathname,
          ...(currentEntity && {
            selectedEntityType: currentEntity.type,
            selectedEntityId: currentEntity.id,
          }),
          ...context,
        };

        const response = await sendAssistantMessage({
          message: message.trim(),
          conversationId,
          context: requestContext,
        });

        // Store conversation ID for subsequent messages
        setConversationId(response.conversationId);

        // Add assistant response to messages
        const assistantMessage: ChatMessage = {
          role: 'assistant',
          content: response.response,
          suggestedAction: response.suggestedAction,
          relatedPages: response.relatedPages,
        };
        setMessages((prev) => [...prev, assistantMessage]);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to send message. Please try again.';
        setError(errorMessage);
        // Remove the user message on error
        setMessages((prev) => prev.slice(0, -1));
      } finally {
        setIsLoading(false);
      }
    },
    [conversationId, location.pathname, currentEntity]
  );

  /**
   * Execute a suggested action from the assistant.
   * Adds a success/error message to the conversation.
   */
  const executeAction = useCallback(
    async (actionId: string): Promise<ExecuteActionResponse> => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await executeAssistantAction(actionId);

        if (response.success) {
          // Add success message to conversation
          const successMessage: ChatMessage = {
            role: 'assistant',
            content: response.message || 'Action completed successfully.',
          };
          setMessages((prev) => [...prev, successMessage]);
        } else {
          setError(response.errorMessage || 'Failed to execute action.');
        }

        return response;
      } catch {
        const errorMessage = 'Failed to execute action. Please try again.';
        setError(errorMessage);
        return { success: false, errorMessage };
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  /**
   * Clear the conversation and reset state.
   */
  const clearConversation = useCallback(() => {
    setMessages([]);
    setConversationId(undefined);
    setError(null);
  }, []);

  return {
    messages,
    isLoading,
    error,
    sendMessage,
    executeAction,
    clearConversation,
  };
}
